import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo, useState } from 'react'
import { Alert, Container, Modal, Button, Card, Breadcrumb, Row, Col, Badge, Table } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Services } from '../../api/Services';
import AlidaLoader from '../../components/common/AlidaLoader';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { getPropertyIcon } from '../../utils';
import UserItem from '../../components/common/UserItem';
import { useAuth } from '../../contexts/auth';
import ScriptEditor from '../../components/common/ScriptEditor';
import { BdaService, BdaServiceProperty } from '../../models/BdaService';
import { Flex, Tooltip } from 'antd';
import AssetProjectsEditor from '../../components/forms/AssetProjectsEditor';
import MDEditor from '@uiw/react-md-editor';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import AppTag from '../../components/common/AppTag';
import EntityDefinitionModal from '../../components/generics/EntityDefinitionModal';

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const [service, setService] = useState<BdaService>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showDefinitionModal, setShowDefinitionModal] = useState(false);
  const [property, setProperty] = useState<BdaServiceProperty>();
  const [error, setError] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);

  const serviceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "services"));

  const { loggedUser } = useAuth();

  const serviceUrl: string = useMemo(() => {
    if (service) {

      const prop = service!.properties.find(prop => prop.key === "spark.kubernetes.container.image");
      return prop?.value || prop?.defaultValue || service!.url;
    }
  }, [service]);

  useEffect(() => {
    Services.getService(+id!)
      .then(
        ({ data }) => {
          Services.getServiceMarkDown(+id!)
            .then(
              ({ data: markdown }) => setService({ ...data, markDown: { value: markdown } }),
              () => setService(data),
            )
        },
        () => {
          showToast('service-error', `Could not get ${serviceLabels.singular}.`, `${serviceLabels.singular} Error`, 'danger');
          setError(true);
        }
      );
    // eslint-disable-next-line
  }, [id]);

  const handleServiceEdit = (clone?: boolean) => () => navigate(`/services/${id}/edit` + (clone ? '?mode=clone' : ''));
  const handleDeleteService = () => setShowDeleteModal(true);
  const handleDeleteClose = () => setShowDeleteModal(false);
  const handlePropertyClose = () => setShowPropertyModal(false);

  const deleteService = () => {
    showLoader();

    Services.deleteService(service!.id!)
      .then(
        () => {
          showToast('service-delete-success', `${serviceLabels.singular} deleted successfully.`, 'Success', 'success');
          hideLoader();
          handleDeleteClose();
          navigate('/services');
        },
        () => {
          showToast('service-delete-error', `Could not delete ${serviceLabels.singular}.`, `${serviceLabels.singular} Error`, 'danger');
          hideLoader();
        }
      );
  };

  const openPropertyModal = (prop: BdaServiceProperty) => () => {
    setProperty(prop);
    setShowPropertyModal(true);
  }

  const openProjectsModal = () => setShowProjectsModal(true);
  const openDefinitionModal = () => setShowDefinitionModal(true);
  const handleProjectsModalClose = () => setShowProjectsModal(false);
  const handleDefinitionModalClose = () => setShowDefinitionModal(false);

  const renderAsyncContent = () => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred during {serviceLabels.singular} retrieve. Try again later.</span>
      </Alert>
    );

    return !service
      ? (
        <div className="d-flex justify-content-center py-5">
          <AlidaLoader size={300} />
        </div>
      )
      : (
        <>
          <Breadcrumb>
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/services' }}>{serviceLabels.plural}</Breadcrumb.Item>
            <Breadcrumb.Item active>{service.name}</Breadcrumb.Item>
          </Breadcrumb>

          <Card className="item-detail-group main bg-service">
            <Flex align="center">
              {service.category && <Badge bg="pink" className="shadow">{service.category.name}</Badge>}
              <Badge className="ms-auto shadow" bg="primary">{service.accessLevel}</Badge>
            </Flex>

            {/* <FontAwesomeIcon className="item-icon" icon={iconsMap.get(service.area!) || solid('chart-pie')} /> */}

            <div className="position-relative p-2">
              <Row className="gx-0 align-items-center justify-content-md-between">
                <Col xs={12} md="auto" className="pe-3">
                  <h3 className="mb-0">{service.name}</h3>
                  <p className="text-trailing mb-3">{service.description}</p>
                </Col>

                <Col xs={12} md="auto" className="text-center text-md-start mb-2 mb-md-0">
                  <Tooltip title="Show Definition">
                    <button className="circle-button" onClick={openDefinitionModal}>
                      <FontAwesomeIcon icon={solid('code')} />
                    </button>
                  </Tooltip>
                  {service.owner!.id === loggedUser!.sub && (
                    <>
                      <Tooltip title="Manage Projects">
                        <button className="circle-button" onClick={openProjectsModal}>
                          <span className="fa-layers">
                            <FontAwesomeIcon icon={solid('folder')} transform="left-14 grow-2" />
                            <FontAwesomeIcon icon={solid('cog')} transform="shrink-4 down-8 right-3" />
                          </span>
                        </button>
                      </Tooltip>
                      <Tooltip title="Clone">
                        <button className="circle-button" onClick={handleServiceEdit(true)}>
                          <FontAwesomeIcon icon={solid('clone')} />
                        </button>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <button className="circle-button" onClick={handleServiceEdit()}>
                          <FontAwesomeIcon icon={solid('pen')} />
                        </button>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <button className="circle-button" onClick={handleDeleteService}>
                          <FontAwesomeIcon icon={solid('trash')} />
                        </button>
                      </Tooltip>
                    </>
                  )}
                </Col>
              </Row>
            </div>

            <Flex className="mb-2">
              <div className="bg-highlight">
                <UserItem className="negative" user={service.owner!} organization={service.organization!} />
              </div>
            </Flex>

            {service.tags.length > 0 && (
              <Flex className="tags-wrapper w-auto mb-2">
                {service.tags.map(tag => <AppTag className="me-1" tag={tag} key={tag} />)}
              </Flex>
            )}

            <Flex align="center" justify="space-between">
              <small className="d-block text-trailing">{serviceUrl}</small>
              <Badge className="service-version" bg="none">{service.version}</Badge>
            </Flex>
          </Card>

          <Card className="p-2 mb-3">
            <h6 className="text-muted mb-3">{serviceLabels.singular} Properties</h6>

            {!service.properties.length && (
              <Alert className="py-2 mb-0">
                <FontAwesomeIcon icon={solid('info-circle')} />
                <span className="ms-3">This {serviceLabels.singular} has no properties.</span>
              </Alert>
            )}

            {service.properties.length > 0 && (
              <Table className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 20 }}></th>
                    <th>Key</th>
                    <th className="text-center">Description</th>
                    <th className="text-center">Type</th>
                    <th className="text-center" style={{ width: 150 }}>Default Value</th>
                    <th className="text-center">Mandatory</th>
                  </tr>
                </thead>
                <tbody>
                  {service.properties.map(property => (
                    <tr key={`prop-${property.key}`}>
                      <td className="text-center">
                        <Badge bg="secondary">
                          <FontAwesomeIcon icon={getPropertyIcon(property.type)} />
                          <span className="ms-2">{property.type}</span>
                        </Badge>
                      </td>
                      <td className="font-monospace">{property.key}</td>
                      <td>
                        <small className="text-muted">{property.description}</small>
                      </td>
                      <td className="text-center">{property.valueType}</td>
                      <td className="text-center">{
                        property.valueType === "JSON"
                          ? (
                            <Button size="sm" variant="light" className="border" onClick={openPropertyModal(property)}>
                              <FontAwesomeIcon icon={solid('eye')} />
                              <span className="ms-2">Show Value</span>
                            </Button>
                          )
                          : property.defaultValue
                      }</td>
                      <td className="text-center">{property.mandatory && <FontAwesomeIcon icon={solid('check-square')} />}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          {service.markDown?.value && (
            <Card className="p-2 ps-3">
              <div data-color-mode="light">
                <MDEditor.Markdown className="px-2" source={service.markDown.value} />
              </div>
            </Card>
          )}
        </>
      );
  }

  return (
    <Container className="mt-3">
      <Helmet>
        <title>{serviceLabels.singular} {id} Detail</title>
      </Helmet>

      {renderAsyncContent()}

      <br />

      <Modal show={showPropertyModal} onHide={handlePropertyClose}>
        <Modal.Header closeButton>
          <Modal.Title>Property '{property?.key}' default value</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{property?.description}</p>

          <ScriptEditor value={property?.defaultValue} readOnly height={350} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handlePropertyClose}>Close</Button>
        </Modal.Footer>
      </Modal>

      <EntityDefinitionModal open={showDefinitionModal} onClose={handleDefinitionModalClose} entity={service} label={serviceLabels.singular} />

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {serviceLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {serviceLabels.singular} <b>{service && service.name}</b>?</p>

          <Alert className="p-2" variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteService}>Delete</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showProjectsModal} onHide={handleProjectsModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{serviceLabels.singular} Projects</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AssetProjectsEditor assetId={+id!} assetType="service" assetLabel={serviceLabels.singular} />
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="light" className="border" onClick={handleProjectsModalClose} size="sm">Cancel</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
